/**
 * The steps before a Rule 506(b) signature (S-30): consent to receive the documents here, the
 * documents of the offering, the confirmation that they were received, questions to the issuer and,
 * for a person who is not accredited, the questions about their experience.
 *
 * The server decides what is missing (`GET /reg-d/cases/{id}/status`); this shows it, does each
 * step and tells the signing screen what is still owed. The sealed texts are shown in English with
 * `translate="no"`: a Spanish rendering that differs from the sealed words would be what the
 * person read and not what they affirmed.
 */
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import regDService, {
  type Questionnaire,
  type RegDStatus,
} from '../../services/regD.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  allAcknowledged,
  allOpened,
  documentTitle,
  hasPackage,
  stepLabels,
  stepsOwed,
} from './regDSteps';

interface Props {
  readonly caseId: string;
  /** The answer to the accreditation questionnaire above: it decides what is owed. */
  readonly category: string | null;
  readonly disabled: boolean;
  /** Called with the steps still owed, once the server has said (null until then). */
  readonly onOwedChange: (owed: readonly string[] | null) => void;
}

const box: CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: '14px 16px',
  background: colors.canvas,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const heading: CSSProperties = { fontSize: 13, fontWeight: 700, color: colors.ink, margin: 0 };
const body: CSSProperties = { fontSize: 13, color: colors.inkSoft, lineHeight: 1.55, margin: 0 };
const fine: CSSProperties = { fontSize: 12, color: colors.muted, lineHeight: 1.5, margin: 0 };
const input: CSSProperties = {
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  background: colors.surface,
  color: colors.ink,
};

function Action({
  label,
  onClick,
  disabled,
  quiet = false,
}: Readonly<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  quiet?: boolean;
}>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        alignSelf: 'flex-start',
        padding: '8px 16px',
        borderRadius: 8,
        border: quiet ? `1px solid ${colors.border}` : 'none',
        background: quiet ? colors.surface : colors.navy,
        color: quiet ? colors.ink : '#FFFFFF',
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );
}

function ConsentStep({
  status,
  disabled,
  run,
}: Readonly<{
  status: RegDStatus;
  disabled: boolean;
  run: (job: () => Promise<void>) => Promise<void>;
}>): JSX.Element {
  const [code, setCode] = useState('');
  const openSample = async (): Promise<void> => {
    const blob = await regDService.getConsentSample();
    globalThis.open(URL.createObjectURL(blob), '_blank', 'noopener');
  };
  if (status.consent.given) {
    return (
      <div style={box}>
        <p style={heading}>Electronic delivery</p>
        <p style={body}>You agreed to receive these documents here.</p>
        <Action
          label="Withdraw my consent"
          quiet
          disabled={disabled}
          onClick={() => void run(() => regDService.withdrawConsent())}
        />
        <p style={fine}>
          If you withdraw, your Account Executive will deliver the documents to you on paper.
        </p>
      </div>
    );
  }
  return (
    <div style={box}>
      <p style={heading}>Agree to receive these documents here</p>
      <p style={body} translate="no">
        {status.consent.text}
      </p>
      <Action label="Open the sample document" quiet disabled={disabled} onClick={() => void run(openSample)} />
      <label htmlFor="regd-code" style={fine}>
        The six-digit code in the sample document
      </label>
      <input
        id="regd-code"
        inputMode="numeric"
        maxLength={6}
        value={code}
        disabled={disabled}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        style={{ ...input, width: 140 }}
      />
      <Action
        label="I agree"
        disabled={disabled || code.length !== 6}
        onClick={() => void run(() => regDService.giveConsent(code))}
      />
      <p style={fine}>
        This is never a condition of your account. If you prefer paper, ask your Account
        Executive.
      </p>
    </div>
  );
}

function DocumentsStep({
  status,
  caseId,
  disabled,
  run,
}: Readonly<{
  status: RegDStatus;
  caseId: string;
  disabled: boolean;
  run: (job: () => Promise<void>) => Promise<void>;
}>): JSX.Element {
  const open = async (documentId: string): Promise<void> => {
    const url = await regDService.getDocumentUrl(caseId, documentId);
    globalThis.open(url, '_blank', 'noopener');
  };
  const acknowledged = allAcknowledged(status.documents);
  return (
    <div style={box}>
      <p style={heading}>The documents of this offering</p>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {status.documents.map((d) => (
          <li key={d.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={body}>{`${documentTitle(d)} · version ${d.version}`}</span>
            {d.delivered && <span style={fine}>Opened</span>}
            <Action
              label="Open"
              quiet
              disabled={disabled || !status.consent.given}
              onClick={() => void run(() => open(d.id))}
            />
          </li>
        ))}
      </ul>
      {acknowledged ? (
        <p style={body}>
          {`You confirmed you received them on ${formatDateTime(status.acknowledgement.acknowledged_at)}.`}
        </p>
      ) : (
        <>
          <p style={body} translate="no">
            {status.acknowledgement.text}
          </p>
          <Action
            label="I received and read these documents"
            disabled={disabled || !status.consent.given || !allOpened(status.documents)}
            onClick={() => void run(() => regDService.acknowledge(caseId))}
          />
        </>
      )}
      {status.earliest_signing_at !== null && (
        <p style={fine}>
          {`You can sign from ${formatDateTime(status.earliest_signing_at)}, so that you have time to read them.`}
        </p>
      )}
    </div>
  );
}

function QuestionsStep({
  status,
  caseId,
  disabled,
  run,
}: Readonly<{
  status: RegDStatus;
  caseId: string;
  disabled: boolean;
  run: (job: () => Promise<void>) => Promise<void>;
}>): JSX.Element {
  const [question, setQuestion] = useState('');
  return (
    <div style={box}>
      <p style={heading}>Questions about this offering</p>
      <p style={body}>
        You can ask about the terms and conditions of the offering, and for any more information
        you need to check what you were given. Your Account Executive answers here.
      </p>
      {status.questions.map((q) => (
        <div key={q.question_id} style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 8 }}>
          <p style={body}>{q.question}</p>
          <p style={fine}>
            {q.answer ?? 'Waiting for an answer.'}
          </p>
        </div>
      ))}
      <textarea
        aria-label="Your question"
        value={question}
        rows={3}
        maxLength={2000}
        disabled={disabled}
        onChange={(e) => setQuestion(e.target.value)}
        style={input}
      />
      <Action
        label="Send my question"
        disabled={disabled || question.trim().length < 5}
        onClick={() =>
          void run(async () => {
            await regDService.askQuestion(caseId, question.trim());
            setQuestion('');
          })
        }
      />
    </div>
  );
}

function SophisticationStep({
  status,
  caseId,
  disabled,
  run,
}: Readonly<{
  status: RegDStatus;
  caseId: string;
  disabled: boolean;
  run: (job: () => Promise<void>) => Promise<void>;
}>): JSX.Element {
  const state = status.sophistication.state;
  const [form, setForm] = useState<Questionnaire | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const editable = state === 'not_submitted' || state === 'not_qualified';

  useEffect(() => {
    if (!editable) return;
    let cancelled = false;
    regDService
      .getQuestionnaire(caseId)
      .then((q) => {
        if (!cancelled) setForm(q);
      })
      .catch(() => {
        // The step shows nothing to fill in; the signature still says what is missing.
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, editable]);

  if (state === 'qualified') {
    return (
      <div style={box}>
        <p style={heading}>Your experience</p>
        <p style={body}>Our team reviewed your answers. You can go on.</p>
      </div>
    );
  }
  if (state === 'awaiting_review') {
    return (
      <div style={box}>
        <p style={heading}>Your experience</p>
        <p style={body}>Our team is reviewing your answers. We will tell you here when it is done.</p>
      </div>
    );
  }
  const complete =
    form !== null && form.items.filter((q) => q.required).every((q) => (answers[q.key] ?? '').trim() !== '');
  return (
    <div style={box}>
      <p style={heading}>Your experience</p>
      <p style={body}>
        To buy without being an accredited investor, you must show that you know enough about
        financial and business matters to judge the merits and risks of this investment on your own.
      </p>
      {state === 'not_qualified' && (
        <p style={{ ...body, color: colors.danger }}>
          Your last answers did not show the experience this offering requires. You can send new
          answers.
        </p>
      )}
      {form?.items.map((q) => (
        <div key={q.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor={`regd-q-${q.key}`} style={body}>
            {q.text}
          </label>
          {q.choices.length > 0 ? (
            <select
              id={`regd-q-${q.key}`}
              value={answers[q.key] ?? ''}
              disabled={disabled}
              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
              style={input}
            >
              <option value="">Choose one</option>
              {q.choices.map((c) => (
                <option key={c} value={c}>
                  {choiceLabel(c)}
                </option>
              ))}
            </select>
          ) : (
            <textarea
              id={`regd-q-${q.key}`}
              rows={3}
              maxLength={2000}
              value={answers[q.key] ?? ''}
              disabled={disabled}
              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
              style={input}
            />
          )}
        </div>
      ))}
      <Action
        label="Send my answers"
        disabled={disabled || !complete}
        onClick={() => void run(() => regDService.submitAnswers(caseId, answers))}
      />
    </div>
  );
}

const CHOICE_LABEL: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  none: 'Never',
  less_than_3_years: 'Less than 3 years',
  '3_to_10_years': '3 to 10 years',
  more_than_10_years: 'More than 10 years',
};

function choiceLabel(code: string): string {
  return CHOICE_LABEL[code] ?? code;
}

export default function RegDPackageSection({
  caseId,
  category,
  disabled,
  onOwedChange,
}: Readonly<Props>): JSX.Element | null {
  const [status, setStatus] = useState<RegDStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const next = await regDService.getStatus(caseId);
      setStatus(next);
      onOwedChange(stepsOwed(next, category));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the steps for this offering.'));
    }
  }, [caseId, category, onOwedChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (job: () => Promise<void>): Promise<void> => {
    setError(null);
    try {
      await job();
      await load();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'That did not work. Please try again.'));
    }
  };

  if (status === null) {
    return error === null ? null : (
      <div role="alert" style={{ ...fine, color: colors.danger }}>
        {error}
      </div>
    );
  }
  const owed = stepsOwed(status, category);
  const nonAccredited = category === 'not_accredited';
  const showDocuments = hasPackage(status) && (nonAccredited || status.accredited_missing.length > 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {owed.length > 0 && (
        <div style={{ ...box, background: colors.warningBg }}>
          <p style={heading}>Before you can sign</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {stepLabels(owed).map((label) => (
              <li key={label} style={body}>
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(showDocuments || owed.includes('consent')) && (
        <ConsentStep status={status} disabled={disabled} run={run} />
      )}
      {showDocuments && <DocumentsStep status={status} caseId={caseId} disabled={disabled} run={run} />}
      {nonAccredited && (
        <SophisticationStep status={status} caseId={caseId} disabled={disabled} run={run} />
      )}
      <QuestionsStep status={status} caseId={caseId} disabled={disabled} run={run} />
      {error !== null && (
        <div role="alert" style={{ ...fine, color: colors.danger }}>
          {error}
        </div>
      )}
    </div>
  );
}
